import { isCancel } from 'axios'
import { makeAutoObservable } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { CreditUsageAnalyticsApi } from 'entities/Analytics/api/creditUsageAnalytics'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { type IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ExportAnalyticsModalStore } from 'widgets/ExportAnalyticsModal'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { CreditUsageChartDataStore } from 'pages/analytics/pages/creditUsage/ui/Chart'
import { CreditUsageTableStore } from './creditUsageTableStore'

export class CreditUsageAnalyticsPageStore {
  private readonly _chartDataStore: CreditUsageChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _requestCancelAxios = new RequestCancelAxios()
  private readonly _tableStore: CreditUsageTableStore
  private readonly _contactsDetailsManageStore: ContactsDetailsManageStore

  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(public readonly entities: IEntitiesStore) {
    this._chartDataStore = new CreditUsageChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [],
      breakdownFilterTypes: [],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      CreditUsageAnalyticsApi.exportAnalytics
    )
    this._tableStore = new CreditUsageTableStore(
      () => this._analyticsFiltersStore.analyticsFiltersParams,
      entities
    )
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(entities)

    makeAutoObservable(this)
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }

  get tableStore() {
    return this._tableStore
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._tableStore.init()
    this._fetchAnalytics('page')
    void this._contactsDetailsManageStore.init()
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      await CreditUsageAnalyticsApi.preloadCreditUsageAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const { data } = await CreditUsageAnalyticsApi.getCreditUsageAnalytics(
        analyticsFiltersParams,
        {
          cancelToken: this._requestCancelAxios.token,
        }
      )

      this._chartDataStore.setRawData(data, period)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(data, 'creditUsage')
      },
    }
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._tableStore.dispose()
    this._requestCancelAxios.reset()
    this._chartDataStore.dispose?.()
    this._exportAnalyticsModalStore.dispose()
    this._contactsDetailsManageStore.reset()
  }
}
